import type { CSSProperties } from 'react';

/**
 * Waits for the next animation frame to ensure DOM is mounted
 */
export function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * Возвращает прямоугольник контейнера для расчёта направления открытия дропдауна.
 *
 * `document.body` (дефолтный контейнер, когда явный `.dropdown-container`
 * не найден) не годится: `getBoundingClientRect()` на body отдаёт размер
 * всего документа, а не видимой области. На длинных страницах body.bottom
 * оказывается далеко за пределами экрана, из-за чего направление
 * рассчитывается так, будто снизу полно места, — дропдаун открывается вверх
 * там, где реального места над тумблером нет, и обрезается верхом экрана/формы.
 */
export function getContainerRect(container: Element | null): {
  top: number;
  left: number;
  right: number;
  bottom: number;
} {
  if (container && container !== document.body) {
    return container.getBoundingClientRect();
  }
  return {
    top: 0,
    left: 0,
    right: document.documentElement.clientWidth,
    bottom: document.documentElement.clientHeight,
  };
}

/**
 * Attempts to get dropdown element with retry logic
 */
export async function getDropdownElement(
  dropdownRef: React.RefObject<HTMLDivElement | null>
): Promise<HTMLDivElement | null> {
  let dropdownEl = dropdownRef.current;
  
  if (!dropdownEl) {
    await nextFrame();
    dropdownEl = dropdownRef.current;
  }
  
  return dropdownEl;
}

/**
 * Creates initial dropdown styles
 */
export function createInitialDropdownStyles(
  baseStyle: CSSProperties,
  widthStyle: Record<string, string>
): CSSProperties {
  return {
    ...baseStyle,
    ...widthStyle,
    visibility: 'hidden',
    position: 'fixed',
  };
}

/**
 * Creates final dropdown styles by merging base and direction styles
 */
export function createFinalDropdownStyles(
  baseStyle: CSSProperties,
  directionStyle: Record<string, string | undefined>
): CSSProperties {
  // Фильтруем undefined значения из directionStyle
  const cleanDirectionStyle: Record<string, string> = {};
  Object.entries(directionStyle).forEach(([key, value]) => {
    if (value !== undefined) {
      cleanDirectionStyle[key] = value;
    }
  });

  const unsetOpposites: Record<string, undefined> = {};
  if ('top' in cleanDirectionStyle) unsetOpposites.bottom = undefined;
  if ('bottom' in cleanDirectionStyle) unsetOpposites.top = undefined;
  if ('left' in cleanDirectionStyle) unsetOpposites.right = undefined;
  if ('right' in cleanDirectionStyle) unsetOpposites.left = undefined;

  return {
    ...baseStyle,
    ...unsetOpposites,
    ...cleanDirectionStyle,
    visibility: 'visible',
  };
}

/**
 * Creates fallback styles when dropdown positioning fails
 */
export function createFallbackStyles(baseStyle: CSSProperties): CSSProperties {
  return {
    ...baseStyle,
    top: '100%',
    left: '0',
    visibility: 'visible',
  };
}